// Whose mailbox a signing email leaves from, and whose name it's
// signed by.
//
// Default, for everyone: whoever clicked Send, from their own
// mailbox, signed with their own Outlook signature — the same
// doctrine as every other 1:1 advisor send in the OS.
//
// THE ONE NAMED EXCEPTION (Grant, 2026-09-09).
//
// Mylah Joy Bella does Insurance admin/carrier work and has NO
// client-facing email identity anywhere else in the OS — no inbox, no
// composer; back_office is deliberately excluded from the whole Email
// section (see web/src/components/sidebar-data.ts). E-signature was
// the one door that let a client-facing email leave as her anyway: she
// initiated a send and it went out as "Mylah Joy Bella" to a client,
// which is exactly what the rest of the OS goes out of its way to
// prevent. In the prior workflow (SignNow, outside the OS), the same
// send left from insurance@stewardship.pro, never from her.
//
// A signing email SHE initiates now goes from the Insurance company
// inbox (insurance@stewardship.pro — already a registered shared
// mailbox, see ../microsoft-graph/shared-mailboxes.ts) and is signed
// as Stewardship Insurance, never with her name.
//
// Scoped to Mylah BY EMAIL, deliberately, not by her back_office role
// or her company scope. Grant's own words: "do not do this for
// backoffice staff. just do it for mylah." A future back_office hire,
// or a role-wide policy, is a separate and deliberate decision — see
// CLAUDE.md's per-user vs OS-wide "deciphering rule" — not something
// this quietly sweeps in. If that decision is ever made, this becomes
// a small list rather than a single constant; it does not become a
// role check.
const MYLAH_EMAIL = "mylahjoy@stewardship.pro";
const MYLAH_SEND_AS_MAILBOX_UPN = "insurance@stewardship.pro";
const MYLAH_SEND_AS_DISPLAY_NAME = "Stewardship Insurance";

export interface EsignSenderIdentity {
  /** Graph mailbox this email should be sent from. Null means the
   *  sender's own mailbox (/me) — the default for everyone. */
  mailboxUpn: string | null;
  /** True (the default) = append the sender's own Outlook signature.
   *  False = the email is signed as a company, not a person, and the
   *  sender's personal signature must not appear under it. */
  useSenderSignature: boolean;
  /** Only meaningful when useSenderSignature is false. */
  companySignOffName: string | null;
}

const DEFAULT_IDENTITY: EsignSenderIdentity = {
  mailboxUpn: null,
  useSenderSignature: true,
  companySignOffName: null,
};

/** Resolve which mailbox + signature a signing email from this sender
 *  should use. Takes an email (not a user id) so it stays a pure,
 *  dependency-free lookup — the field-types.ts rule this module
 *  follows the same way send-signers.ts does — and every caller
 *  already has the sender's email at hand or one query away. */
export function resolveEsignSenderIdentity(
  senderEmail: string | null | undefined,
): EsignSenderIdentity {
  if (senderEmail && senderEmail.trim().toLowerCase() === MYLAH_EMAIL) {
    return {
      mailboxUpn: MYLAH_SEND_AS_MAILBOX_UPN,
      useSenderSignature: false,
      companySignOffName: MYLAH_SEND_AS_DISPLAY_NAME,
    };
  }
  return DEFAULT_IDENTITY;
}

/** The person's name a CLIENT may see as the sender of a signing
 *  request, or null when no person should be named. The signing page's
 *  "X at Stewardship Insurance sent this to you" line reads this, so a
 *  send signed as the company (Mylah's) names the company only — the
 *  same rule the emails follow (Grant, 2026-09-25: "i do not want her
 *  name to show up on anything going to customers"). */
export function clientFacingSenderName(
  senderName: string | null | undefined,
  senderEmail: string | null | undefined,
): string | null {
  if (resolveEsignSenderIdentity(senderEmail).companySignOffName) return null;
  return senderName ?? null;
}
